/*
 * Copyright (c) 2026. Bolmate B.V.
 */
import './style/notificationInput.scss';

import React, {MouseEventHandler} from 'react';
import {useTranslation} from 'react-i18next';

import {ChangeFunction} from '@dvrd/dvr-controls';

import {BolmateButton, BolmateDialog, BolmateInput, BolmateNumberInput} from '../../../controls';

interface Props<T> {
    onActivate: MouseEventHandler;
    onClose: VoidFunction;
    onChange: ChangeFunction<T>;
    onSubmit: VoidFunction;
    value: T;
    parentValue: T;
    label: string;
    active: boolean;
    disabled?: boolean;
    inputProps?: Record<string, any>;
}

export default function NotificationInput<T extends number | string>(props: Props<T>) {
    const {t} = useTranslation();
    const {onActivate, onClose, onChange, onSubmit, value, label, active, inputProps, disabled, parentValue} = props;

    function renderInput() {
        if (typeof value === 'string')
            return (
                <BolmateInput
                    onChange={onChange as ChangeFunction<string>}
                    value={value}
                    onEnter={onSubmit}
                    autoFocus
                    shape='rounded'
                    {...inputProps}
                />
            );
        return (
            <BolmateNumberInput
                onChange={onChange as ChangeFunction<number>}
                value={value as number}
                onEnter={onSubmit}
                autoFocus
                asNumber
                shape='rounded'
                {...inputProps}
            />
        );
    }

    return (
        <>
            <BolmateButton
                outlined
                size='normal'
                label={parentValue}
                onClick={onActivate}
                disabled={disabled}
                className='products-button'
            />
            <BolmateDialog active={active} onClose={onClose} title={label} className='notification-input' hideCancel>
                {renderInput()}
                {/* Own footer instead of the dialog's default, so cancel can
                    sit on the left edge and confirm on the right. Square
                    shapes (the default) throughout — tertiary cancel +
                    primary confirm. */}
                <div className='notification-input-footer'>
                    <BolmateButton
                        tertiary
                        label={t('notifications.components.cancel', 'Annuleren')}
                        onClick={onClose}
                    />
                    <BolmateButton label={t('notifications.components.confirm', 'Bevestigen')} onClick={onSubmit} />
                </div>
            </BolmateDialog>
        </>
    );
}
